import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { LanguageToggle } from '@/features/welcome/components/LanguageToggle';
import { useEnrolmentSession } from '@/lib/enrolmentSession';
import { useKioskSession } from '@/lib/kioskSession';
import { inQueueForm, sessionCandidateFromYourName } from '@/lib/sessionCandidate';
import { HANDOFF_CONTROL } from '@/services/handoff.contract';
import { DIAGNOSIS } from '@/services/identityLookup';

import { DeadEndHelpContext } from './deadEndHelp';
import { ExitButton } from './ExitButton';
import { ExitConfirmDialog } from './ExitConfirmDialog';
import { NeedHelpDialog } from './NeedHelpDialog';
import { NeedHelpFab } from './NeedHelpFab';

export interface KioskScreenProps {
  readonly children: ReactNode;
  /** The help affordance is absent on Welcome, present from check-in onward [Figma frames]. */
  readonly showHelp?: boolean;
  /**
   * The session-ending control in the top bar.
   *
   * Present on every flow screen and **hidden on exactly two**, which is the design's own rule rather than
   * a judgement call: the `Exit button` instance is set `visible: false` on `Welcome (tap to start)`
   * [`12438:26978`] and on `03 · Routing`, the lookup's checking state [`12438:27524`], and visible on all
   * 68 other frames. Both exclusions read the same way — Welcome IS the cleared state, so there is nothing
   * to exit from, and the checking state already offers Back, which is the same escape without the
   * question.
   */
  readonly showExit?: boolean;
  /**
   * ⭐ **This screen is a SIGN-IN DEAD END, so a *Need help?* press here names `needHelpAtSignIn`** (*stuck at
   * sign-in*) rather than `needHelpOverlay` (*asked for a rep*) [0461 § Decision; 0171]. Off by default, so a
   * new screen reads *asked for a rep* until a record says otherwise.
   *
   * Passed by exactly three screens — the one list, derived from 0168 (the sign-in error and lockout screens)
   * and 0230 § Decision 1 (every identity-failure dead end, the neutral hand-off included):
   * - `ThrottledScreen` — the lockout [0064; 0168; KID-7 scenario 1];
   * - `SeeARepScreen` — the neutral hand-off, where an errored or timed-out lookup and a barred record land
   *   [KID-2; 0230 § Decision 1 and 3];
   * - `CheckInScreen`, **only while it shows its inline lookup error** (`rejectedFactors`, the api's refusal)
   *   [the Operator, 2026-10-14, «9. A»: a press there counts as stuck at sign-in].
   *
   * ⛔ Not a screen id: `SCREEN.routing` also hosts the different-email prompt, which continues. ⚠️ Until KID-6 gives the ambiguous match its own screen (0230 § Decision 2), it
   * lands on `SeeARepScreen` like every other `assistance` cause — the api names none [0045] — so a press there
   * reads *stuck at sign-in*, not *multiple profiles*. KID-6 decides what its screen passes.
   */
  readonly signInDeadEnd?: boolean;
  /**
   * ⭐ **Reaching this screen raises the entry, with no tap** [0500 point 1; 03-us-kid-7 scenario *Reaching the
   * neutral hand-off raises the entry*]. It raises exactly what the screen's *Need help?* press and its own *Get
   * help* button raise — the same `summonRepresentative`, the same control (`signInDeadEnd` decides it), no name
   * — and it does NOT open the overlay: the screen itself is the confirmation [0500 point 3].
   *
   * Passed by exactly one screen, `SeeARepScreen` — the neutral hand-off, whose copy already says a representative
   * has been told [0220 L1]. ⛔ A named exception to 0220 L2 (the second, after the closing screen's 0343), so it
   * is never a default: the lockout and the check-in form's inline lookup error still raise only on a tap [0500 §
   * *What it amends in 0230*].
   *
   * Once per candidate session [KOP-1 AC-14; 0500 point 4]: `summonRepresentative` is the cap, so a StrictMode
   * double effect, a return to the screen, or a later *Get help* / *Need help?* press sends nothing more — the
   * press then opens the overlay on `in the recruiter queue` (O4) [0500 point 5]. A write that did not land
   * gives that back: the store resets its flag, and the next human tap raises again [0500 point 3; 0220 L1 — the
   * promise must have an entry, and a write that did not land gives the human control back].
   *
   * ⭐ **On such a screen the pill is *Get help*** [0500 point 5; 0230 point 3; 0457 point 4]: raise-if-not-raised,
   * then open straight on O4, on every path. Otherwise a failed arrival write (the flag reset) would open the pill
   * on its launch state and a landed one on O4, so the stage the lobby sees would tell an outage from a barred
   * record. Now it never depends on the write's outcome, and a pill tap after a failed write still re-sends.
   *
   * ⚠️ **Why this lives here and not in the screen**, unlike the rule *the screen that promises a person raises
   * the entry* (`FinishScreen`, `DrugTestScreen`, `SubmissionFailedScreen`): the control this raise names is
   * DERIVED here, from `signInDeadEnd`, and it must be the one the pill and *Get help* name. A screen whose arrival
   * raise names its own fixed control (a later screen, e.g. under 0502) keeps the screen-owned
   * `useEffect(() => summonRepresentative(undefined, <control>))` those screens use, and does not need this prop.
   */
  readonly raisesOnArrival?: boolean;
}

/**
 * The page every card sits on: the `--surface` background, the language bar aligned to the card's right
 * edge, and the "Need help?" pill anchored bottom-right.
 *
 * `--surface` (#f2f4f6) rather than `--muted` (#f6f5f4) — the frames use a cooler grey behind the card than
 * the one used for muted fills, and the two are close enough that reusing the wrong one looks like a bug
 * only once both are on screen together.
 */
export function KioskScreen({
  children,
  showHelp = true,
  showExit = true,
  signInDeadEnd = false,
  raisesOnArrival = false,
}: KioskScreenProps) {
  /*
   * The Exit question is drawn HERE and only here, and it is asked from two places: the top bar's `Exit`
   * and every Back that would leave the visit [ISS-149]. The second lives in the screen components, whose
   * hooks run above this one, so the open/closed state is on the store (`exitAskedOn`) rather than in a
   * `useState` this component alone could set — see the field for why it holds a SCREEN.
   *
   * It stays per-screen-visit, as it was: shown only while it was asked on the screen now showing, and
   * cleared when this unmounts, so an open question cannot survive into a screen the candidate did not
   * ask it on.
   */
  const confirmingExit = useKioskSession(
    (state) => state.exitAskedOn !== undefined && state.exitAskedOn === state.screen,
  );
  const openConfirm = useKioskSession((state) => state.askToExit);
  const dismissExit = useKioskSession((state) => state.dismissExit);
  useEffect(() => dismissExit, [dismissExit]);
  /*
   * The help dialog is per-screen-visit state for the same reason the Exit question is: `App` swaps whole
   * components, so moving screen unmounts this and an overlay opened on one screen cannot survive into the
   * next. What DOES survive the screen change is `recruiterSummoned`, which is on the session store because
   * it is a fact about the candidate.
   *
   * ⚠️ **Local to THIS instance, not keyed by screen id, on purpose** [KID-7 review round 1]. Two components
   * render under one id — `CheckingScreen` and `CheckInScreen` are both `checkIn` — so a screen-keyed flag
   * opened on the first would reopen on the second. A dead end's own help button opens it through
   * {@link DeadEndHelpContext}, which this instance provides to the card inside it.
   */
  const [askingForHelp, setAskingForHelp] = useState(false);
  const openHelp = useCallback(() => {
    setAskingForHelp(true);
  }, []);
  const closeHelp = useCallback(() => {
    setAskingForHelp(false);
  }, []);
  /*
   * ⚠️ **Read here rather than inside the dialog, and that is deliberate.** The dialog is a presentation
   * component the tests can render on its own; the rule it obeys — one open call per candidate [KOP-1
   * AC-14] — belongs to the store, and this is the seam that hands it over. `summonRepresentative` enforces
   * the cap itself either way; `alreadyQueued` is what stops the dialog ASKING for a name it could not
   * deliver.
   */
  const recruiterSummoned = useKioskSession((state) => state.recruiterSummoned);
  const summonRepresentative = useKioskSession((state) => state.summonRepresentative);
  const enterSessionCandidate = useKioskSession((state) => state.enterSessionCandidate);
  /** The check-in surname, when one was typed this session — *Your name*'s initial when it is one word. */
  const checkInSurname = useKioskSession((state) => state.factors.lastName);
  /*
   * `endCandidateSession` and nothing else. It replaces the whole candidate slice with its initial value,
   * which is exactly what OQ-101 rules Exit means on this side of the wire: what is on the DEVICE goes, and
   * what already reached Bullhorn stays — there is no deletion path and none was asked for [0033].
   */
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);

  /*
   * ⭐ **Every press names a control, and the screen decides which** [0461 § Decision]: *stuck at sign-in* at a
   * sign-in dead end, *asked for a rep* anywhere else. Both submits below send it — the kiosk names the
   * control, never a reason or a state [KOP-1d AC-2].
   */
  const needHelpControl = signInDeadEnd
    ? HANDOFF_CONTROL.needHelpAtSignIn
    : HANDOFF_CONTROL.needHelpOverlay;
  /*
   * ⭐ **The submit of *Your name* is also a name ENTERED** [03-us-kad-6j-f AC-1a; 0382 § Ruling 1]: the live
   * socket re-sends `session.started` with the candidate it gives, and a name that gives none sends nothing.
   * ⛔ Never per keystroke — the dialog calls this once, on submit.
   */
  const waitForAssistance = useCallback(
    (name: string) => {
      enterSessionCandidate(sessionCandidateFromYourName(name, checkInSurname));
      summonRepresentative(name, needHelpControl);
    },
    [checkInSurname, enterSessionCandidate, needHelpControl, summonRepresentative],
  );
  /* O2: a candidate with a record raises with no name, naming the same control as the name step. */
  const talkToRecruiter = useCallback(() => {
    summonRepresentative(undefined, needHelpControl);
  }, [needHelpControl, summonRepresentative]);
  /*
   * ⭐ **A dead end's own help button: one tap raises, then the overlay opens on its queue state** [03-us-kid-7
   * first scenario and its 2026-10-06 note; 0230]. Raise FIRST — `recruiterSummoned` is set synchronously, so
   * the dialog mounts on `in the recruiter queue`. It names `needHelpControl`, the SAME control the pill names
   * here, so the screen's `signInDeadEnd` is the one list both controls read [KID-7 review round 1].
   */
  const deadEndHelp = useMemo(
    () => ({
      askForHelp: () => {
        summonRepresentative(undefined, needHelpControl);
        openHelp();
      },
    }),
    [needHelpControl, openHelp, summonRepresentative],
  );
  /*
   * ⭐ **The neutral hand-off raises on arrival** [0500 point 1]: the raise `askForHelp` makes, without opening the
   * overlay. No ref, as on the closing screen (`FinishScreen`): the store's flag is set synchronously, so a
   * double-invoked effect and a later press both find the entry already raised [KOP-1 AC-14]. Fire-and-forget
   * [0046; BR-K7] — a failed write leaves the screen exactly as it is. It is O2's raise (`talkToRecruiter`): the
   * same control and no name, so the arrival, the pill and *Get help* send the same body, and *Get help* and the
   * arrival share the one call that sends it.
   */
  useEffect(() => {
    if (raisesOnArrival) talkToRecruiter();
  }, [raisesOnArrival, talkToRecruiter]);
  /*
   * When the overlay asks a name, and when it says there is no record [0441 point 4; 03-us-kid-7 O2/O3]. The
   * name step needs only the absence of a `candidateId`; the sentence «We don't have a record for you yet…»
   * needs the lookup to have ANSWERED `new` — never the bare absence, which also covers the lockout, the
   * neutral hand-off and a session that has not looked up yet (the Operator, 2026-10-06, 3.B).
   */
  const hasRecord = useKioskSession((state) => state.lookupResult?.candidateId !== undefined);
  const knowsNoRecord = useKioskSession(
    (state) =>
      state.lookupResult?.diagnosis === DIAGNOSIS.new &&
      state.lookupResult.candidateId === undefined,
  );
  /*
   * The box copies the recruiter's queue row [0441 point 5; the Operator, 2026-10-07, 1.A], rendered from
   * what the kiosk holds — it never reads the pane. *Name* is the session's name in 0216's form, which wins
   * over a typed one [KOP-1d AC-5]; the O3 submit sets it through `enterSessionCandidate` before it raises.
   * Without one — on Welcome, where no session takes a name — it is the typed name the raise carried, cut as
   * the api cuts it (`summonedName`); «Anonymous» only when neither is left.
   * ⭐ **All of that yields to the name `/handoff` answered** (`queuedEntryName`) [ISS-38]: a press that JOINED an
   * entry already holding a name reads that name on the recruiter's row, so the box does too. Until the answer
   * lands, or when an older api sends none, the box is as above.
   * *Site* is `queueKioskAttribution`'s format: venue, then kiosk, joined by « · », a missing part left out.
   */
  const sessionCandidate = useKioskSession((state) => state.sessionCandidate);
  const summonedName = useKioskSession((state) => state.summonedName);
  const queuedEntryName = useKioskSession((state) => state.queuedEntryName);
  const venueName = useEnrolmentSession((state) => state.venueName);
  const kioskName = useEnrolmentSession((state) => state.kioskName);
  const recruiterSees = useMemo(() => {
    const site = [venueName, kioskName].filter((part) => part !== undefined && part !== '');
    return {
      name:
        queuedEntryName ??
        (sessionCandidate === null ? summonedName : inQueueForm(sessionCandidate)),
      site: site.length === 0 ? undefined : site.join(' · '),
    };
  }, [kioskName, queuedEntryName, sessionCandidate, summonedName, venueName]);

  return (
    /*
     * `pt-0` below `sm`, 24px from `sm` up — a responsive rule read from the frames, not a preference.
     * Every MOBILE frame starts its language bar flush at y=0 (`01 · Welcome — Mobile` 12021:40340,
     * `02 · Check-in — Mobile` 12021:40354, `12 · What brings you in today? — Mobile` 12195:11602, `14 · Active associate — Mobile` 12021:41184), while
     * every iPad and desktop frame insets it by 24. Applying the tablet's padding at 375 pushed the whole
     * screen down and the structural diff reported it once per element — nine findings for one cause.
     *
     * `gap-0` below `sm` for the same reason, and this one has ONE owner on purpose. The distance between
     * the language bar and the card is not the same number at every width, and it is not the same number on
     * every screen: the frames put the card 16px below the bar on iPad and desktop, 24px below it on a
     * mobile screen that has a header, and flush against it on `01 · Welcome — Mobile` (12021:40340), which
     * has none. Only `KioskCard` knows whether a screen has a header, so `KioskCard` owns the mobile value
     * (`mt-6 sm:mt-0`, applied only in the header branch) and this gap owns the `sm`-and-up value. Setting
     * both meant they ADDED — 16 + 24 = 40 where the design asked for 24 — and because the offset lands
     * above everything else, it pushed every element on every 375 screen down by the same 16px and the diff
     * reported it once per element. Change one of these two and check the other.
     */
    <main className="relative flex min-h-screen flex-col items-center gap-0 bg-[var(--surface)] pb-8 pt-0 font-sans sm:gap-4 sm:px-4 sm:pt-6">
      {/*
        The frame's `Top bar`: 52 tall, `p(16, 24, 4, 24)`, Exit hard against the card's left edge and the
        language selector hard against its right — which the padding above already matched exactly before
        Exit existed, so this row grows a control rather than changing shape.

        `items-center`, not `items-start`: the bar's content box is 32 tall (the language selector sets it)
        and the frame centres the 29-tall Exit pill inside it, at y=42 in a bar that opens at y=24. With
        `items-start` it sat 1.5px high — inside tolerance, but wrong for the reason the whole row is
        measured. The selector is unaffected either way, being the tallest child.

        `justify-end` when Exit is hidden, because `justify-between` cannot right-align a lone child — it
        would throw the language selector to the LEFT edge on Welcome and on the checking state. The same
        trap `KioskCard`'s header hit with a lone status chip, and the same fix stated in the one place that
        knows whether there are two children.
      */}
      <div
        className={`flex w-full max-w-[553px] items-center px-6 pb-1 pt-4 min-[1440px]:max-w-[768px] ${showExit ? 'justify-between' : 'justify-end'}`}
      >
        {showExit && <ExitButton onClick={openConfirm} />}
        <LanguageToggle />
      </div>

      <DeadEndHelpContext.Provider value={deadEndHelp}>{children}</DeadEndHelpContext.Provider>

      {/* The frames draw the open overlay where the pill sits, and no pill beside it. */}
      {showHelp && !askingForHelp && (
        <NeedHelpFab onClick={raisesOnArrival ? deadEndHelp.askForHelp : openHelp} />
      )}

      {confirmingExit && (
        <ExitConfirmDialog onExit={endCandidateSession} onKeepGoing={dismissExit} />
      )}

      {askingForHelp && (
        <NeedHelpDialog
          alreadyQueued={recruiterSummoned}
          hasRecord={hasRecord}
          knowsNoRecord={knowsNoRecord}
          onClose={closeHelp}
          onTalkToRecruiter={talkToRecruiter}
          onWaitForAssistance={waitForAssistance}
          recruiterSees={recruiterSees}
        />
      )}
    </main>
  );
}
